import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes as colType, SmallSpinner, dateTranslate as formatDate, ratio, rvERA, columnTypes } from 'yankees-react-utils';
import { useOktaQuery, PlayerReport } from '../../../../data';
import PitchLog from './PitchLog';
import VideoPlayerButton from '../../../../components/VideoPlayerButton';

const StyledNavContainer = styled.div`
&& {
    .card-header-tabs {
        margin: 0;

        a {
            border-bottom: none;
            font-weight: bold;
        }

        a.active {
            background-color: #C6D3DC;
            border: 1px solid #ADB0B2;
            border-bottom: none;
        }
    }
}
`;

const query = async (accessToken, playerid, yearid, levelid, pitchtype, fxsubtype) => {
    const ptbfxDrillDownGamesParams = { playerid, yearid, levelid, pitchtype, fxsubtype };
    const ptbfxDrillDownGamesPromise = PlayerReport.ptbfxdrilldown(accessToken, ptbfxDrillDownGamesParams);
    const games = await ptbfxDrillDownGamesPromise;
    return games;
};

const GameLog = ({ playerid, yearid, levelid, pitchtype, fxsubtype, isInBats, useSubType }) => {
    const { t: translate } = useTranslation();
    const [activeTab, setActiveTab] = useState('game-log');
    const { data, status } = useOktaQuery(['gamelog', { playerid, yearid, levelid, pitchtype, fxsubtype }], (t) => query(t, playerid, yearid, levelid, pitchtype, useSubType === true ? fxsubtype : null));

    if (status === 'loading') {
        return <div><SmallSpinner /> {translate('common.loading')}...</div>;
    }
    const rows = data.stats.map((g) => {
        const drilldown = (
            <PitchLog key={`pitchlog-${g.gameid}-${playerid}`} playerid={playerid} gameid={g.gameid} pitchtype={pitchtype} fxsubtype={useSubType === true ? fxsubtype : null} isInBats={isInBats} />
        );

        const videoparams = { pitcherid: playerid, gameid: g.gameid, fxpitchtype: pitchtype, fxsubtype: useSubType === true ? fxsubtype : null };
        const videoplaylist = isInBats ? (<VideoPlayerButton {...videoparams} />) : '-';
        return { ...g, drilldown, videoplaylist };
    });

    const gamesColumns = [
        { id: 'videoplaylist', title: null, type: colType.NODE },
        { id: 'game', title: translate('sections.reports.pitcher.game'), transform: (row) => (row.gamedate ? formatDate(translate, row.gamedate) : row.yearid), type: colType.TEXT },
        { id: 'opp', title: translate('baseball.abbr-opponent'), transform: (row) => (row.ishome === 1 ? row.awayteamabbr : `@${row.hometeamabbr}`), type: colType.TEXT },
        { id: 'pitches', title: translate('sections.reports.pitcher.abbr-num-pitches') },
        { id: 'pit%', title: translate('sections.reports.pitcher.usage-pct'), transform: (row) => ratio(row.pitches, row.totalpitches), type: colType.PERCENT },
        { id: 'initspeed54', title: translate('sections.reports.pitcher.abbr-velo'), precision: 1 },
        { id: 'breakx54', title: translate('sections.reports.pitcher.horiz-break'), precision: 1 },
        { id: 'breakz54', title: translate('sections.reports.pitcher.vert-break'), precision: 1 },
        { id: 'spinrate', title: translate('sections.reports.pitcher.spin-rate') },
        { id: 'spindirectionclock', title: translate('sections.reports.pitcher.spin-dir'), type: columnTypes.TEXT, headerClass: 'text-center', cellClass: 'text-center' },
        { id: 'gyrodegree', title: translate('sections.reports.pitcher.gyro-degree'), precision: 1 },
        { id: 'initposx', title: translate('sections.reports.pitcher.rel-side'), precision: 1 },
        { id: 'initposz', title: translate('sections.reports.pitcher.rel-height'), precision: 1 },
        { id: 'extension', title: translate('sections.reports.pitcher.abbr-extension'), precision: 2 },
        { id: 'stk', title: translate('sections.reports.pitcher.abbr-strike'), transform: (row) => ratio(row.stk, row.pitches), type: colType.PERCENT },
        { id: 'pfxkzon%', title: translate('sections.reports.pitcher.zone-pct'), transform: (row) => ratio(row.expstk_all, row.exppit_all), type: colType.PERCENT },
        { id: 'closepitch%', title: translate('sections.reports.pitcher.close-pitch-pct'), transform: (row) => ratio(row.closepitch, row.pitches), type: colType.PERCENT },
        { id: 'wastepitch%', title: translate('sections.reports.pitcher.waste-pitch-pct'), transform: (row) => ratio((row.wastepitch || row.noncomppitches), row.pitches), type: colType.PERCENT },
        { id: 'ms%', title: translate('sections.reports.pitcher.abbr-miss-pct'), transform: (row) => ratio(row.ms, row.sw), type: colType.PERCENT },
        { id: 'calledk%', title: translate('sections.reports.pitcher.called-k-pct'), transform: (row) => ratio(row.tkstk, row.pitches), type: colType.PERCENT },
        { id: 'chase%', title: translate('sections.reports.pitcher.chase-pct'), transform: (row) => ratio(row.ch, row.outz), type: colType.PERCENT },
        { id: 'putaway%', title: translate('sections.reports.pitcher.put-away-pct'), transform: (row) => ratio(row.so, row.twostrikepitches), type: colType.PERCENT },
        { id: 'gb%', title: translate('baseball.abbr-groundball-pct'), transform: (row) => ratio(row.gb, row.bip), type: colType.PERCENT },
        // { id: 'so', title: 'K' },
        { id: 'hitvelocity', title: translate('sections.reports.pitcher.exit-velo'), precision: 1 },
        { id: 'wellhit%', title: translate('sections.reports.pitcher.well-hit-pct'), transform: (row) => ratio((row.wellhit_exctm95 >= 0 ? row.wellhit_exctm95 : row.wellhit), row.pawhitfx), type: colType.PERCENT },
        { id: 'hitfxera', title: translate('sections.reports.pitcher.hitfx-era'), transform: (row) => rvERA(row.rvexp), precision: 2 },
        { id: 'pfxera', title: translate('sections.reports.pitcher.pfx-era'), transform: (row) => rvERA(row.rvpitchfx), precision: 2 },
        { id: 'locationera', title: translate('sections.reports.pitcher.location-era'), transform: (row) => rvERA(row.rvloc), precision: 2 },
        { id: 'stuffera', title: translate('sections.reports.pitcher.stuff-era'), transform: (row) => rvERA(row.rvstuff), precision: 2 },
        { id: 'breakera', title: translate('sections.reports.pitcher.break-era'), transform: (row) => rvERA(row.rvbreak), precision: 2 },
    ];

    const gameStats = (
        <DataTable
            sortable={false}
            columns={gamesColumns}
            rows={rows}
            striped
        />
    );

    const splitsColumns = [
        { id: 'splitname', title: translate('sections.reports.pitcher.split'), type: colType.TEXT },
        { id: 'pitches', title: translate('sections.reports.pitcher.abbr-num-pitches') },
        { id: 'pit%', title: translate('sections.reports.pitcher.usage-pct'), transform: (row) => ratio(row.pitches, row.totalpitches), type: colType.PERCENT },
        { id: 'initspeed54', title: translate('sections.reports.pitcher.abbr-velo'), precision: 1 },
        { id: 'breakx54', title: translate('sections.reports.pitcher.horiz-break'), precision: 1 },
        { id: 'breakz54', title: translate('sections.reports.pitcher.vert-break'), precision: 1 },
        { id: 'spinrate', title: translate('sections.reports.pitcher.spin-rate') },
        { id: 'spindirectionclock', title: translate('sections.reports.pitcher.spin-dir'), type: columnTypes.TEXT, headerClass: 'text-center', cellClass: 'text-center' },
        { id: 'gyrodegree', title: translate('sections.reports.pitcher.gyro-degree'), precision: 1 },
        { id: 'initposx', title: translate('sections.reports.pitcher.rel-side'), precision: 1 },
        { id: 'initposz', title: translate('sections.reports.pitcher.rel-height'), precision: 1 },
        { id: 'extension', title: translate('sections.reports.pitcher.abbr-extension'), precision: 2 },
        { id: 'stk', title: translate('sections.reports.pitcher.abbr-strike'), transform: (row) => ratio(row.stk, row.pitches), type: colType.PERCENT },
        { id: 'pfxkzon%', title: translate('sections.reports.pitcher.zone-pct'), transform: (row) => ratio(row.expstk_all, row.exppit_all), type: colType.PERCENT },
        { id: 'closepitch%', title: translate('sections.reports.pitcher.close-pitch-pct'), transform: (row) => ratio(row.closepitch, row.pitches), type: colType.PERCENT },
        { id: 'wastepitch%', title: translate('sections.reports.pitcher.waste-pitch-pct'), transform: (row) => ratio((row.wastepitch || row.noncomppitches), row.pitches), type: colType.PERCENT },
        { id: 'ms%', title: translate('sections.reports.pitcher.abbr-miss-pct'), transform: (row) => ratio(row.ms, row.sw), type: colType.PERCENT },
        { id: 'calledk%', title: translate('sections.reports.pitcher.called-k-pct'), transform: (row) => ratio(row.tkstk, row.pitches), type: colType.PERCENT },
        { id: 'chase%', title: translate('sections.reports.pitcher.chase-pct'), transform: (row) => ratio(row.ch, row.outz), type: colType.PERCENT },
        { id: 'putaway%', title: translate('sections.reports.pitcher.put-away-pct'), transform: (row) => ratio(row.so, row.twostrikepitches), type: colType.PERCENT },
        { id: 'gb%', title: translate('baseball.abbr-groundball-pct'), transform: (row) => ratio(row.gb, row.bip), type: colType.PERCENT },
        { id: 'so', title: translate('baseball.abbr-strikeout-k') },
        { id: 'hitvelocity', title: translate('sections.reports.pitcher.exit-velo'), precision: 1 },
        { id: 'wellhit%', title: translate('sections.reports.pitcher.well-hit-pct'), transform: (row) => ratio((row.wellhit_exctm95 >= 0 ? row.wellhit_exctm95 : row.wellhit), row.pawhitfx), type: colType.PERCENT },
        { id: 'hitfxera', title: translate('sections.reports.pitcher.hitfx-era'), transform: (row) => rvERA(row.rvexp), precision: 2 },
        { id: 'pfxera', title: translate('sections.reports.pitcher.pfx-era'), transform: (row) => rvERA(row.rvpitchfx), precision: 2 },
        { id: 'locationera', title: translate('sections.reports.pitcher.location-era'), transform: (row) => rvERA(row.rvloc), precision: 2 },
        { id: 'stuffera', title: translate('sections.reports.pitcher.stuff-era'), transform: (row) => rvERA(row.rvstuff), precision: 2 },
        { id: 'breakera', title: translate('sections.reports.pitcher.break-era'), transform: (row) => rvERA(row.rvbreak), precision: 2 },
    ];

    const basicSplits = data.splits.filter((x) => x.splitcategory === 1 || (x.splitcategory === 4 && [7, 8, 10, 12].indexOf(x.splitsortorder) >= 0) || x.split === 240);
    const extendedSplits = data.splits.filter((x) => x.splitcategory === 2);
    const pointInGameSplits = data.splits.filter((x) => x.splitcategory === 3);
    const situationalSplits = data.splits.filter((x) => x.splitcategory === 4);
    const byHitterTypesSplits = data.splits.filter((x) => x.splitcategory === 5);
    return (
        <StyledNavContainer>
            <Tabs
                id={`game-log-${playerid}-${yearid}-${pitchtype}`}
                activeKey={activeTab}
                onSelect={(a) => setActiveTab(a)}
            >
                <Tab eventKey="game-log" title={translate('sections.reports.pitcher.game-log')}>{gameStats}</Tab>
                <Tab eventKey="basic-splits" title={translate('sections.reports.pitcher.basic-splits')}>
                    <DataTable columns={splitsColumns} rows={basicSplits} striped={false} />
                </Tab>
                <Tab eventKey="extended-splits" title={translate('sections.reports.pitcher.extended-splits')}>
                    <DataTable columns={splitsColumns} rows={extendedSplits} striped={false} />
                </Tab>
                <Tab eventKey="point-in-game-splits" title={translate('sections.reports.pitcher.point-in-game-splits')}>
                    <DataTable columns={splitsColumns} rows={pointInGameSplits} striped={false} />
                </Tab>
                <Tab eventKey="situational-splits" title={translate('sections.reports.pitcher.situational-splits')}>
                    <DataTable columns={splitsColumns} rows={situationalSplits} striped={false} />
                </Tab>
                <Tab eventKey="by-hitter-type-splits" title={translate('sections.reports.pitcher.by-hitter-splits')}>
                    <DataTable columns={splitsColumns} rows={byHitterTypesSplits} striped={false} />
                </Tab>
            </Tabs>
        </StyledNavContainer>
    );
};

GameLog.propTypes = {
    playerid: PropTypes.number.isRequired,
    yearid: PropTypes.number.isRequired,
    levelid: PropTypes.number.isRequired,
    pitchtype: PropTypes.number.isRequired,
    fxsubtype: PropTypes.number,
    isInBats: PropTypes.bool.isRequired,
    useSubType: PropTypes.bool,
};

GameLog.defaultProps = {
    fxsubtype: null,
    useSubType: false,
};

export default GameLog;
